import type { Dispatch, SetStateAction } from "react"

import type { QueryKey } from "@tanstack/react-query"

import type { TTranslationKeys } from "$/i18n/i18n"

import type { IParticipantOnboardingCoachStepCoachesQueryModel as ICoachStepCoachesQueryModel } from "@/3514/pages/participant.onboarding"
import { ESessionExtraPartyRole } from "@/3514/types"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

enum ESessionScheduleModalScreenPhase {
    Form = "form",
    Picker = "picker"
}

enum ESessionScheduleModalMode {
    Schedule = "schedule",
    Reschedule = "reschedule"
}

enum ESessionScheduleModalOrigin {
    Sessions = "sessions",
    Onboarding = "onboarding"
}

type TSessionScheduleModalExtraPartyData = {
    id: number | null
    email: string | null
    firstName: string | null
    lastName: string | null
    label: string | null
    role: ESessionExtraPartyRole
    invitationStatus: string | null
    attendanceStatus: string | null
}

type TSessionScheduleModalProps =
    | {
          coach: ICoachStepCoachesQueryModel
          user: { id: number; photo: string | null; firstName: string; lastName: string }
          isExtraPartySession: boolean
          sessionId: number
          queryKey?: never
          participantId: number
          onCloseAfterSuccess: TEmptyAsyncCallback
          onTimePickSuccess: (params: { startTime: string; tzId: string }) => Promise<void>
          origin: ESessionScheduleModalOrigin.Onboarding
          mode: ESessionScheduleModalMode.Schedule
          withCloseOnEscapeClick: false
      }
    | {
          coach?: never
          user: { id: number; photo: string | null; firstName: string; lastName: string }
          isExtraPartySession: boolean
          sessionId: number
          queryKey: QueryKey
          participantId: number
          onCloseAfterSuccess?: never
          onTimePickSuccess?: never
          origin: ESessionScheduleModalOrigin.Sessions
          mode: ESessionScheduleModalMode
          withCloseOnEscapeClick: true
      }

type TSessionScheduleModalBodyProps = {
    onCoachTimeQueryError(textKey: keyof TTranslationKeys): void
    onCoachTimeQuerySuccess(textKey: keyof TTranslationKeys): void
    modalScreenPhaseCurrent: ESessionScheduleModalScreenPhase
    setModalScreenPhaseCurrent: Dispatch<SetStateAction<ESessionScheduleModalScreenPhase>>
}

type TSessionScheduleModalTitleVariant = "schedule" | "reschedule" | "attendance"

type TSessionScheduleModalMutationHintVariant =
    | "generic-error"
    | "occupied-time-error"
    | "schedule-success"
    | "reschedule-success"

type TSessionScheduleModalBodyTextState = {
    queryErrorMessage: keyof TTranslationKeys | string
    mutationSuccessHint: TSessionScheduleModalMutationHintVariant
    mutationErrorHint: TSessionScheduleModalMutationHintVariant
    mutationErrorMessage: keyof TTranslationKeys | string
    buttonLabel: keyof TTranslationKeys
}

export { ESessionScheduleModalScreenPhase, ESessionScheduleModalMode, ESessionScheduleModalOrigin }
export type {
    TSessionScheduleModalProps,
    TSessionScheduleModalBodyProps,
    TSessionScheduleModalBodyTextState,
    TSessionScheduleModalTitleVariant,
    TSessionScheduleModalMutationHintVariant,
    TSessionScheduleModalExtraPartyData
}
